import React, { FC, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { GetPaymentMethodConfigResponseType } from '@clearcover/core-app-types';
import { env } from 'Shared/constants/env';
import {
  AdyenCheckout,
  translations,
  useAdyenScripts,
} from 'Shared/hooks/useAdyenScripts';
import { useApi } from 'Shared/services/Api';
import { PaymentMethodType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import { PurchaseFormValuesType } from '../../../types';
import { AdyenCreditCardResponseType } from './types';
import styles from './styles.module.css';

type AdyenFormProps = {
  config?: PaymentMethodType['configuration'];
  onPurchaseSubmit(
    formValues: PurchaseFormValuesType,
    paymentToken: string
  ): void;
};

type BillingAddress = {
  numberOrName?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode: string;
  countryCode?: string;
};

type FormattedCardData = {
  type: 'ENCRYPTED_CREDIT_CARD';
  cardHolder: string;
  number: string;
  cvc: string;
  expirationMonth: string;
  expirationYear: string;
  billingAddress?: BillingAddress;
};

function cardConfiguration(principalInsuredName: string) {
  const config = {
    hasHolderName: true,
    holderNameRequired: true,
    billingAddressRequired: env.adyenCardAvsMode !== 'none',
    billingAddressMode: env.adyenCardAvsMode,
    data: {
      holderName: principalInsuredName,
    },
  };

  if (env.adyenCardAvsMode !== 'full') return config;

  return {
    ...config,
    billingAddressAllowedCountries: ['US'],
    data: {
      ...config.data,
      billingAddress: { country: 'US' },
    },
  };
}

// https://docs.adyen.com/checkout/drop-in-web
export const AdyenForm: FC<AdyenFormProps> = ({ config, onPurchaseSubmit }) => {
  const [currentPlanId, setCurrentPlanId] = useState<string>();
  const [currentQuoteId, setCurrentQuoteId] = useState<string>();
  const [checkout, setCheckout] = useState<AdyenCheckout>();
  const { values, setFieldValue } = useFormikContext<PurchaseFormValuesType>();
  const { scriptLoaded } = useAdyenScripts();
  const { details } = useAppContext();
  const { onPaymentError } = usePurchaseContext();
  const [{ data: adyenConfig, loading: adyenConfigLoading }] = useApi<
    GetPaymentMethodConfigResponseType
  >(
    `/payments/config?configDiscoveryParams=${details.configDiscoveryParams ||
      ''}`
  );

  React.useEffect(() => {
    async function initializeAdyenCheckout() {
      if (
        !scriptLoaded ||
        !config ||
        values.adyenForm ||
        adyenConfigLoading ||
        !currentPlanId
      )
        return;

      const adyenConfiguration = {
        ...adyenConfig.config,
        paymentMethodsConfiguration: {
          card: cardConfiguration(details.principalInsuredName),
        },
        translations: translations,
        environment: env.adyenEnv,
        setStatusAutomatically: false,
        clientKey: env.adyenClientKey,
        onSubmit: (encryptedData: AdyenCreditCardResponseType) => {
          /*
           * Note: Adyen gives us the encrypted values here,
           * it has NOT processed the payment
           * Reznor will attempt to process the payment with the encrypted values
           * If there is a processing failure, it will not appear here.
           */

          if (encryptedData.isValid) {
            const cardData: FormattedCardData = {
              type: 'ENCRYPTED_CREDIT_CARD',
              cardHolder: encryptedData.data.paymentMethod.holderName,
              number: encryptedData.data.paymentMethod.encryptedCardNumber,
              cvc: encryptedData.data.paymentMethod.encryptedSecurityCode,
              expirationMonth:
                encryptedData.data.paymentMethod.encryptedExpiryMonth,
              expirationYear:
                encryptedData.data.paymentMethod.encryptedExpiryYear,
            };

            const adyenBillingAddress = encryptedData.data.billingAddress;

            if (adyenBillingAddress?.postalCode) {
              const billingAddress: BillingAddress = {
                postalCode: adyenBillingAddress.postalCode,
              };

              if (env.adyenCardAvsMode === 'full') {
                billingAddress.numberOrName =
                  adyenBillingAddress.houseNumberOrName;
                billingAddress.street = adyenBillingAddress.street;
                billingAddress.city = adyenBillingAddress.city;
                billingAddress.state = adyenBillingAddress.stateOrProvince;
                billingAddress.countryCode = adyenBillingAddress.country;
              }

              cardData.billingAddress = billingAddress;
            }

            onPurchaseSubmit(values, JSON.stringify(cardData));
          }
        },
        onError: (error: { message: string }) => {
          console.error('Adyen Error', error);
          onPaymentError(error.message ?? 'An error occurred');
        },
      };

      setCheckout(await window.AdyenCheckout(adyenConfiguration));
    }

    initializeAdyenCheckout();
  }, [
    adyenConfig,
    adyenConfigLoading,
    config,
    currentPlanId,
    onPurchaseSubmit,
    details.principalInsuredName,
    scriptLoaded,
    values,
    onPaymentError,
  ]);

  React.useEffect(() => {
    if (!checkout) return;

    const dropin = checkout.create('dropin').mount('#credit-card-form');
    setFieldValue('adyenForm', dropin);
  }, [checkout, setFieldValue]);

  useEffect(() => {
    if (values.paymentPlanId !== currentPlanId) {
      // Must reset dropin when frequency changes but still Adyen
      setFieldValue('adyenForm', null);
      setCurrentPlanId(values.paymentPlanId);
    }

    // Each time the flexible payment frequency changes, a new quote_id is generated
    // so we must reset and reinitialize the dropin using the new quote_id.
    // This ensures we can bind the latest generated quote.
    if (values.quoteId !== currentQuoteId) {
      setFieldValue('adyenForm', null);
      setCurrentQuoteId(values.quoteId);
    }
  }, [
    currentPlanId,
    currentQuoteId,
    setFieldValue,
    values.paymentPlanId,
    values.quoteId,
  ]);

  if (
    values.paymentPlanId !== currentPlanId ||
    values.quoteId !== currentQuoteId
  ) {
    // Must clear div from the DOM for Adyen to properly reset
    return null;
  }

  return (
    <div
      data-testid="adyen-credit-card-form"
      id="credit-card-form"
      className={styles.wrapper}
    />
  );
};
